import React, { useRef } from 'react'
import { Box } from '@chakra-ui/react'
import { DropTargetMonitor, useDrag, useDrop } from 'react-dnd'

import NewSidebarItem from './new-sidebar-item'
import { usePinnedPages } from './pinned-pages-provider'
import { ResolvedPinnedPage } from './new-tabs'
import { DRAGGABLE_ITEM_TYPES } from '~components/shared/drag-and-drop/constants'
import { handleVerticalMovement } from '~components/shared/drag-and-drop/helpers'
import { ListItemType } from '~components/shared/drag-and-drop/types'

interface Props {
    item: ResolvedPinnedPage
    index: number
    /** Whether the sidebar itself is pinned open (controls label visibility). */
    isSidebarPinned: boolean
    setSubmenu: (value: React.SetStateAction<string>) => void
}

/**
 * A Pinned-submenu row that can be drag-and-drop reordered. The whole row is
 * the drag handle; plain clicks still follow the link and the hover pin
 * button still unpins, since those never start a drag.
 */
const DraggablePinnedRow: React.FC<Props> = ({
    item,
    index,
    isSidebarPinned,
    setSubmenu,
}) => {
    const ref = useRef<HTMLDivElement>(null)
    const { movePinnedPage } = usePinnedPages()

    const [{ isDragging }, drag] = useDrag({
        type: DRAGGABLE_ITEM_TYPES.PinnedSidebarPage,
        item: () => ({ id: item.page.to ?? item.page.label, index }),
        collect: monitor => ({
            isDragging: monitor.isDragging(),
        }),
    })

    const [, drop] = useDrop<ListItemType, void, {}>({
        accept: DRAGGABLE_ITEM_TYPES.PinnedSidebarPage,
        hover(dragged: ListItemType, monitor: DropTargetMonitor) {
            if (!ref.current) return
            handleVerticalMovement(ref, index, movePinnedPage, dragged, monitor)
        },
    })

    drag(drop(ref))

    return (
        <Box
            ref={ref}
            w="full"
            opacity={isDragging ? 0.4 : 1}
            cursor="grab"
            _active={{ cursor: 'grabbing' }}
        >
            <NewSidebarItem
                page={item.page}
                sectionLabel={item.sectionLabel}
                isSidebarPinned={isSidebarPinned}
                showDragHandle
                isSubPage
                setSubmenu={setSubmenu}
            />
        </Box>
    )
}

export default DraggablePinnedRow
